import { useState } from "react";
import type { ImageData } from "../types";
import { toDataUrl } from "./ImageUpload";
import { resolveTeamPalette } from "../lib/teamPalette";

/**
 * Standard logo size tokens (px). Prefer a token over a raw number so every
 * surface stays visually consistent; a raw number is still accepted for
 * back-compat / one-off cases.
 */
const LOGO_SIZE_TOKENS = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 48,
  xl: 64,
} as const;

type TeamLogoSizeToken = keyof typeof LOGO_SIZE_TOKENS;
type TeamLogoSize = TeamLogoSizeToken | number;

function resolveLogoSizePx(size: TeamLogoSize): number {
  return typeof size === "number" ? size : LOGO_SIZE_TOKENS[size];
}

interface TeamLogoProps {
  team: {
    id?: string | number;
    team_id?: string | number;
    short_name?: string;
    name: string;
    logo_url?: string | null;
    logo_image?: ImageData | null;
  };
  size?: TeamLogoSize;
  className?: string;
  /**
   * When true AND a real (SVG) logo is present, render the logo without the
   * boxed background/padding/clipping — a bare shield on the panel. Has no
   * effect on the initials fallback, which always keeps its colored badge
   * box. Defaults to false (existing boxed behavior).
   */
  bare?: boolean;
  /**
   * Override the rendered `data-testid` (applies to both the SVG `<img>`
   * and the initials-fallback `<div>`). Use when a page mounts more than
   * one `TeamLogo` for the same team within a shared test scope — e.g. a
   * CSS-only responsive variant that stays in the DOM alongside the
   * primary one — so a scoped `getByTestId("team-logo-img")` locator stays
   * unique. Defaults to the existing "team-logo-img" / "team-logo-fallback"
   * pair, unchanged for every caller that doesn't pass this.
   */
  testId?: string;
}

/**
 * Team logos are SVG-only (hard cutover). A `logo_url` that doesn't end in
 * `.svg`, or a legacy `logo_image` whose mime_type isn't `image/svg+xml`, is
 * treated as absent and falls back to the initials badge — it is never
 * rendered, even though the bytes may still exist server-side.
 */
function resolveSvgLogoSrc(team: TeamLogoProps["team"]): string | null {
  if (team.logo_url && team.logo_url.toLowerCase().endsWith(".svg")) {
    return team.logo_url;
  }
  if (team.logo_image?.mime_type === "image/svg+xml") {
    return toDataUrl(team.logo_image);
  }
  return null;
}

export default function TeamLogo({
  team,
  size = "sm",
  className = "",
  bare = false,
  testId,
}: TeamLogoProps) {
  const [broken, setBroken] = useState(false);
  const px = resolveLogoSizePx(size);
  const src = broken ? null : resolveSvgLogoSrc(team);

  if (src) {
    if (bare) {
      return (
        <img
          src={src}
          alt={team.short_name ?? team.name}
          className={`object-contain ${className}`}
          style={{ width: px, height: px, minWidth: px }}
          data-testid={testId ?? "team-logo-img"}
          onError={() => setBroken(true)}
        />
      );
    }
    return (
      <div
        className={`flex items-center justify-center rounded bg-white/5 p-0.5 ${className}`}
        style={{ width: px, height: px, minWidth: px }}
      >
        <img
          src={src}
          alt={team.short_name ?? team.name}
          className="w-full h-full object-contain rounded"
          data-testid={testId ?? "team-logo-img"}
          onError={() => setBroken(true)}
        />
      </div>
    );
  }

  const initials = (team.short_name ?? team.name).slice(0, 3).toUpperCase();
  const palette = resolveTeamPalette(team);
  const fontSize = px < 24 ? "text-[8px]" : px < 40 ? "text-xs" : "text-sm";

  // A club that has supplied its own colours gets its own badge. The hashed
  // `fallbackClass` is only a stand-in for teams with no colours on record —
  // using it for a team that DOES have colours put a pale blue crest beside
  // a red team name on the public share header, which reads as two
  // different clubs rather than one.
  const usesOwnColors = palette.isCustom;
  const colorClass = usesOwnColors ? "" : palette.fallbackClass;

  return (
    <div
      className={`flex items-center justify-center rounded font-bold select-none ${colorClass} ${fontSize} ${className}`}
      style={{
        width: px,
        height: px,
        minWidth: px,
        ...(usesOwnColors
          ? { backgroundColor: palette.primary, color: palette.contrast }
          : {}),
      }}
      data-testid={testId ?? "team-logo-fallback"}
      title={team.name}
    >
      {initials}
    </div>
  );
}

export type { TeamLogoProps, TeamLogoSize, TeamLogoSizeToken };
export { LOGO_SIZE_TOKENS };
